import { useState } from "react";

import "./stylesDashboardBarChart.css";
import RebetLoader from "../../RebetLoader/RebetLoader";

import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  BarElement,
  LineElement,
} from "chart.js";

import { formatNumber } from "chart.js/helpers";
import { Skeleton, Select } from "antd";
import CustomDatePicker from "../../DatePicker/DatePicker";

function DashboardBarChart({
  title = false,
  data,
  display = false,
  headers = [],
  customOptions = false,
  isApiRunning = false,
  setSelected,
  getData,
  dates,
  setDates,
  showCustomDatePicker = false,
  showDropdown = false,
  showPresets = false,
  datePickerStartRange,
  customLegend = null,
  CustomDatePickerComponent = null,
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedDataset, setSelectedDataset] = useState("All");

  const handeSetActiveIndex = (title, index) => {
    setActiveIndex(index);
    setSelected(title);
  };

  // Function to filter data based on the dropdown selection
  const getFilteredData = () => {
    if (!data || !data.datasets) return data;
    
    if (selectedDataset === "All") {
      return data;
    }
    
    const filteredDatasets = data.datasets.filter(
      (dataset) => dataset.label === selectedDataset
    );
    
    return {
      ...data,
      datasets: filteredDatasets,
    };
  };

  // Get dropdown options based on the available datasets
  const getDropdownOptions = () => {
    const options = [{ value: "All", label: "All" }];
    
    if (data && data.datasets) {
      data.datasets.forEach((dataset) => {
        options.push({
          value: dataset.label,
          label: dataset.label,
        });
      });
    }
    
    return options;
  };

  const handleDropdownChange = (value) => {
    setSelectedDataset(value);
  };

  ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    Title,
    Tooltip,
    Legend
  );

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    scales: {
      x: {
        beginAtZero: true,
        stacked: true,
      },
      y: {
        stacked: true,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      datalabels: {
        display,
        anchor: "end",
        align: "start",
        formatter: (value) => value.toLocaleString(),
        color: "black",
        font: {
          family: "Poppins-regular",
          weight: "bold",
        },
      },
      tooltip: {
        enabled: true,
        mode: "index",
        intersect: false,
        backgroundColor: "#252729",
        titleFont: {
          family: "Poppins-regular",
          size: 16,
          weight: "bold",
        },
        bodyFont: {
          family: "Poppins-regular",
          size: 14,
        },
        padding: 15,
        displayColors: true,
        callbacks: {
          label: function (tooltipItem) {
            const label = tooltipItem.dataset.label || "";
            const value = tooltipItem.raw;
            return `${label}: ${formatNumber(value)}`;
          },
        },
      },
    },
  };

  return (
    <div id="key-features-chart" >
      <div className="key-features-chart-header">
        <div className="dashboard-line-chart-header">
          {headers.map((kpi, index) => (
            <div
              key={index}
              className={`dashboard-line-chart-kpi ${
                activeIndex === index ? "active" : ""
              }`}
              onClick={() => handeSetActiveIndex(kpi.title, index)}
            >
              <div className="dashboard-line-chart-kpi-title">{kpi.title}</div>
              {isApiRunning ? (
                <div style={{ marginTop: 20 }}>
                  <Skeleton.Button
                    active={1}
                    size={10}
                    shape={"default"}
                    block={false}
                  />
                </div>
              ) : (
                <div className="dashboard-line-chart-kpi-value">
                  {formatNumber(kpi.amount, "en-US", {
                    style: "decimal",
                    maximumFractionDigits: 2,
                  })}
                  {kpi.percentage && (
                    <div
                      className={`dashboard-line-chart-kpi-percentage ${
                        kpi.percentage >= 0 ? "up" : "down"
                      }`}
                  >
                    {kpi.percentage > 0
                      ? `▲${kpi.percentage}%`
                      : `▼${kpi.percentage}%`}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          {title && <h3>{title}</h3>}
          {customLegend && customLegend}
        </div>
        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          {showDropdown && (
            <Select
            value={selectedDataset}
            onChange={handleDropdownChange}
            options={getDropdownOptions()}
            style={{ width: 150 }}
              placeholder="Select dataset"
            />
          )}
          {showCustomDatePicker && CustomDatePickerComponent ? (
            CustomDatePickerComponent
          ) : showCustomDatePicker ? (
            <CustomDatePicker
              getData={getData}
              dates={dates}
              setDates={setDates}
              showPresets={showPresets}
              startRange={datePickerStartRange}
            />
          ) : null}
        </div>
      </div>
      {isApiRunning ? (
        <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <RebetLoader height={"100px"} width={"100px"} />
        </div>
      ) : (
        <div className="key-features-chart-body">
          <Bar data={getFilteredData()} options={customOptions || options} />
        </div>
      )}
    </div>
  );
}

export default DashboardBarChart;
